// CategoryBreadcrumb.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { SanitizedLink } from '../component/SanitizedLink.jsx';
import ResetListStyle from '../style/ResetListStyle.js';
import { CategoryListType } from '../resource/categoryConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { color } from '../style/variables.js';
import media, { hover } from '../style/media.js';

const tabI18nKey = {
  [CategoryListType.STORY]: 'stories',
  [CategoryListType.FLIX]: 'title_flix',
  [CategoryListType.SHORT]: 'shorts',
  [CategoryListType.IMAGE]: 'profile_photo',
};

const CategoryBreadcrumb = ({ categoryId, listType }) => {
  const { t } = useTranslation();

  const overviewLabel = t('category_title', {
    ns: TranslationNamespace.FEED,
  });
  const categoryLabel = t(`category_${categoryId}`, {
    ns: TranslationNamespace.FEED,
  });
  const listTypeLabel =
    listType && listType !== CategoryListType.ALL && tabI18nKey[listType]
      ? t(tabI18nKey[listType], { ns: TranslationNamespace.FEED })
      : null;

  return (
    <Nav aria-label="breadcrumb">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink to="/category-overview">
            {overviewLabel}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <Separator aria-hidden="true">/</Separator>
        {listTypeLabel ? (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink to={`/category/${categoryId}`}>
                {categoryLabel}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <Separator aria-hidden="true">/</Separator>
            <BreadcrumbItem aria-current="page">
              <BreadcrumbCurrent>{listTypeLabel}</BreadcrumbCurrent>
            </BreadcrumbItem>
          </>
        ) : (
          <BreadcrumbItem aria-current="page">
            <BreadcrumbCurrent>{categoryLabel}</BreadcrumbCurrent>
          </BreadcrumbItem>
        )}
      </BreadcrumbList>
    </Nav>
  );
};

CategoryBreadcrumb.propTypes = {
  categoryId: PropTypes.string.isRequired,
  listType: PropTypes.string,
};

CategoryBreadcrumb.defaultProps = {
  listType: null,
};

const Nav = styled.nav`
  padding-top: 32px;
  margin-bottom: 24px;
  ${media.mobile`
    padding-top: 16px;
  `};
`;

const BreadcrumbList = styled.ol`
  ${ResetListStyle}
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
`;

const Separator = styled.span`
  color: ${color.grey[300]};
  font-size: 14px;
`;

const BreadcrumbLink = styled(SanitizedLink)`
  color: ${color.grey[300]};
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.014px;
  text-decoration: none;

  ${hover`
      text-decoration: underline;
    color: ${color.white};
    `}
`;

const BreadcrumbCurrent = styled.span`
  color: ${color.grey[300]};
  font-size: 14px;
`;

export { CategoryBreadcrumb };
export default CategoryBreadcrumb;
